/**
 * Keyboard handoff (PROD-1278 feedback, item 1).
 *
 * iOS Safari raises the software keyboard for `input.focus()` only while a
 * user gesture is running. Opening Serve from Today is not one gesture: the
 * tap navigates, the route chunk downloads, the session fetches, and only
 * then is there a search input worth focusing. A `focus()` at that point
 * leaves a caret in the field and the keyboard DOWN, so the worker has to tap
 * the field before the first kid — the exact slowdown the redesign is for.
 *
 * WebKit does keep the keyboard up when focus moves from one text input to
 * another, gesture or not. So the tap focuses a real, off-screen text input
 * (the BRIDGE) synchronously — that raises the keyboard inside the gesture —
 * and the serve page moves focus from the bridge to its own search the moment
 * that input is ready; seconds later is fine. Whatever the worker (or the kid
 * holding the iPad) types into the bridge meanwhile travels with the focus.
 *
 * One bridge per document, registered by `KeyboardHandoffInput`. The module
 * holds no React state: it is a thin seam over `document.activeElement` that
 * a test can drive with a plain DOM.
 *
 * Limits: a deep link or a reload has no gesture, so the field ends up
 * focused with the keyboard down until the worker taps it once. Nothing on
 * the web can change that.
 */

let bridge: HTMLInputElement | null = null
/** A gesture armed the bridge and no screen has taken the handoff yet. */
let pending = false

function activeElement(): Element | null {
  return typeof document === 'undefined' ? null : document.activeElement
}

/** Called from the bridge input's ref: the element on mount, `null` on unmount. */
export function registerKeyboardHandoffInput(input: HTMLInputElement | null): void {
  if (input === null) {
    pending = false
  }
  bridge = input
}

/**
 * Focus the bridge synchronously — call this INSIDE the tap that navigates to
 * a screen whose search must open with the keyboard up, before anything
 * async. Returns whether the bridge took focus (false with no mounted bridge,
 * or when the browser refused).
 */
export function armKeyboardHandoff(): boolean {
  if (!bridge) {
    pending = false
    return false
  }
  bridge.value = ''
  bridge.focus({ preventScroll: true })
  pending = activeElement() === bridge
  return pending
}

/** The bridge still holds focus and no screen has taken the handoff. */
export function isKeyboardHandoffPending(): boolean {
  return pending && bridge !== null && activeElement() === bridge
}

/**
 * The receiving screen's half: the text typed into the bridge since it was
 * armed, cleared on read. Call it right before focusing the real input,
 * whether or not the bridge still has focus (the worker may have tapped
 * elsewhere in between — the text is still theirs).
 */
export function takeKeyboardHandoffText(): string {
  const text = bridge?.value ?? ''
  if (bridge) {
    bridge.value = ''
  }
  pending = false
  return text
}

/**
 * Drop the handoff: the screen that took over has no search to give the
 * keyboard to (a school-orders-only campus, an empty preview, the School
 * orders tab), or is unmounting. Blurs the bridge only while it still holds
 * focus, so a worker already typing in the real field is left alone.
 */
export function releaseKeyboardHandoff(): void {
  if (bridge) {
    bridge.value = ''
    if (activeElement() === bridge) {
      bridge.blur()
    }
  }
  pending = false
}
